import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import client, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Bot, Plus, Trash2, SendHorizontal, ChevronDown, ChevronRight, ShieldCheck, ShieldAlert, Terminal, Loader2 } from "lucide-react";
import { CloudIcon, ProviderIcon } from "@/components/brand-icons";
import { cn } from "@/lib/utils";
import { ConfirmDeleteDialog } from "@/components/confirm-delete-dialog";

const SUGGESTIONS = [
  "How many virtual machines do I have, and in which regions?",
  "Which of my resources violate our governance rules (regions, tags, naming)?",
  "Which resource groups have no tags?",
  "List the repositories in our Git connector and tell me which ones have no README",
];

function SessionPane({ sessions, active, onSelect, onNew, onDelete }) {
  return (
    <aside className="sticky top-0 flex h-screen w-[300px] min-w-[300px] max-w-[300px] shrink-0 flex-col self-start overflow-hidden border-r border-slate-200 bg-white" data-testid="patrice-session-pane">
      <p className="flex h-16 items-center border-b border-slate-200 px-4 font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">// conversations · {sessions.length}</p>
      <div className="border-b border-slate-200 p-2">
        <Button onClick={onNew} className="h-9 w-full justify-start bg-slate-900 text-white hover:bg-slate-800" data-testid="patrice-new-session">
          <Plus className="mr-2 h-4 w-4" /> New conversation
        </Button>
      </div>
      <ul className="flex-1 overflow-y-auto p-2">
        {sessions.length === 0 && <li className="px-3 py-6 text-center text-xs text-slate-400">No conversation yet</li>}
        {sessions.map((s) => {
          const isActive = s.id === active;
          return (
            <li key={s.id} className="relative">
              <button type="button" onClick={() => onSelect(s.id)} data-testid={`patrice-session-${s.id}`} aria-pressed={isActive}
                className={cn("flex w-full flex-col gap-0.5 px-3 py-2.5 pr-10 text-left transition-colors", isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100")}>
                <span className="truncate text-sm font-semibold">{s.title}</span>
                <span className={cn("font-mono-tight text-[10px] uppercase tracking-widest", isActive ? "text-slate-300" : "text-slate-400")}>{new Date(s.updated_at).toLocaleString()}</span>
              </button>
              <button type="button" onClick={() => onDelete(s.id)} title="Delete conversation" aria-label="Delete conversation" data-testid={`patrice-delete-${s.id}`}
                className={cn("absolute right-2 top-1/2 -translate-y-1/2 p-1.5 transition-colors", isActive ? "text-slate-300 hover:text-white" : "text-slate-400 hover:bg-slate-200 hover:text-rose-700")}>
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}

function ConnIcon({ c, className }) {
  return c?.kind === "git" ? <ProviderIcon provider={c.platform} className={className} /> : <CloudIcon cloud={c?.platform} className={className} />;
}

function QueryDetails({ meta }) {
  const [open, setOpen] = useState(false);
  if (!meta?.cli && !meta?.output && !meta?.error) return null;
  const status = meta.status;
  return (
    <div className="mt-3 border border-slate-200 bg-slate-50 text-xs" data-testid="patrice-query-details">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center gap-2 px-3 py-2 text-left font-mono-tight text-[11px] text-slate-600 hover:text-slate-900" data-testid="patrice-toggle-details">
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        {meta.connector && <span className="inline-flex items-center gap-1"><ConnIcon c={meta.connector} className="h-3.5 w-3.5" />{meta.connector.name}</span>}
        <span className={cn("ml-auto inline-flex items-center gap-1 uppercase tracking-widest", status === "ok" ? "text-emerald-700" : status === "blocked" ? "text-amber-700" : "text-rose-700")}>
          {status === "blocked" ? <ShieldAlert className="h-3 w-3" /> : <ShieldCheck className="h-3 w-3" />}
          {status === "ok" ? "executed · read-only" : status === "blocked" ? "blocked · not executed" : "failed"}
        </span>
      </button>
      {meta.cli && (
        <div className="flex items-start gap-2 border-t border-slate-200 px-3 py-2 font-mono-tight text-[12px] text-slate-800" data-testid="patrice-cli">
          <Terminal className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" /><code className="break-all">{meta.cli}</code>
        </div>
      )}
      {open && (meta.output || meta.error) && (
        <pre className="terminal max-h-80 overflow-auto border-t border-slate-200 p-3 text-[12px]" data-testid="patrice-raw-output">{meta.error || meta.output}</pre>
      )}
    </div>
  );
}

function Message({ m }) {
  const isUser = m.role === "user";
  return (
    <div className={cn("flex gap-3", isUser && "justify-end")} data-testid={`patrice-message-${m.role}`}>
      {!isUser && <div className="grid h-8 w-8 shrink-0 place-items-center bg-slate-900 text-white"><Bot className="h-4 w-4" /></div>}
      <div className={cn("max-w-[78%] px-4 py-3 text-sm", isUser ? "bg-slate-900 text-white" : "border border-slate-200 bg-white text-slate-800")}>
        {isUser ? <p className="whitespace-pre-wrap">{m.content}</p> : (
          <div className="patrice-md">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
          </div>
        )}
        {!isUser && <QueryDetails meta={m.meta} />}
      </div>
    </div>
  );
}

export default function Patrice() {
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [platformsList, setPlatformsList] = useState([]);
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    client.get("/ai/sessions").then((r) => { setSessions(r.data); if (r.data[0]) setActive(r.data[0].id); });
    client.get("/ai/connectors").then((r) => setPlatformsList(r.data));
  }, []);

  useEffect(() => {
    if (!active) { setMessages([]); return; }
    client.get(`/ai/sessions/${active}/messages`).then((r) => setMessages(r.data));
  }, [active]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const newSession = async () => {
    const { data } = await client.post("/ai/sessions", {});
    setSessions((l) => [data, ...l]);
    setActive(data.id);
    return data.id;
  };

  const [deleting, setDeleting] = useState(null);
  const deleteSession = async (id) => {
    await client.delete(`/ai/sessions/${id}`);
    setSessions((l) => l.filter((s) => s.id !== id));
    if (active === id) setActive(null);
    toast.success("Conversation deleted");
  };

  const ask = async (text) => {
    const q = (text ?? question).trim();
    if (!q || busy) return;
    setBusy(true);
    setQuestion("");
    const optimistic = { id: "tmp", role: "user", content: q, meta: {} };
    setMessages((l) => [...l, optimistic]);
    try {
      const sid = active || (await newSession());
      const { data } = await client.post(`/ai/sessions/${sid}/ask`, { question: q });
      setMessages((l) => [...l.filter((m) => m.id !== "tmp"), data.user_message, data.assistant_message]);
      setSessions((l) => l.map((s) => (s.id === sid ? { ...s, title: data.title || s.title, updated_at: data.assistant_message.created_at } : s)).sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1)));
    } catch (err) {
      setMessages((l) => l.filter((m) => m.id !== "tmp"));
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); }
  };

  return (
    <div className="grid grid-cols-[300px_minmax(0,1fr)]" data-testid="patrice-layout">
      <SessionPane sessions={sessions} active={active} onSelect={setActive} onNew={newSession} onDelete={(id) => setDeleting(sessions.find((s) => s.id === id))} />
      <ConfirmDeleteDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} name={deleting?.title || ""} title="Delete conversation" verb="Delete conversation"
        description="All messages and query results in this conversation will be removed." onConfirm={() => deleteSession(deleting.id)} testid="confirm-session" />
      <div className="flex h-screen min-w-0 flex-col">
        <div className="flex items-end justify-between gap-4 border-b border-slate-200 px-10 py-6">
          <div>
            <p className="font-mono-tight text-xs uppercase tracking-widest text-slate-500">// assistant</p>
            <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight" data-testid="page-title">Patrice AI</h1>
            <p className="mt-1 text-sm text-slate-600">Ask about your real cloud estate or Git repositories. Patrice translates your question into an <code className="font-mono-tight">az</code> / <code className="font-mono-tight">aws</code> / <code className="font-mono-tight">gcloud</code> query, runs it read-only through a connector and explains the result — checking what it finds against your governance rules.</p>
          </div>
          <div className="flex shrink-0 items-center gap-2" data-testid="patrice-connectors">
            {platformsList.length === 0 ? (
              <span className="border border-amber-200 bg-amber-50 px-3 py-1.5 font-mono-tight text-[11px] text-amber-800">No connector — add one under Connectors</span>
            ) : platformsList.map((p) => (
              <span key={p.id} className="inline-flex items-center gap-1.5 border border-slate-200 bg-white px-2.5 py-1 font-mono-tight text-[11px] text-slate-700" title={`${p.kind === "git" ? "Git" : "Platform"} connector${p.ok ? "" : " · last test failed"}`} data-testid={`patrice-connector-${p.id}`}>
                <ConnIcon c={p} className="h-3.5 w-3.5" /> {p.name}
                <span className={cn("h-1.5 w-1.5", p.ok ? "bg-emerald-500" : "bg-amber-400")} />
              </span>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-10 py-6" data-testid="patrice-messages">
          {messages.length === 0 && !busy ? (
            <div className="mx-auto mt-10 max-w-2xl text-center" data-testid="patrice-empty">
              <div className="mx-auto grid h-12 w-12 place-items-center bg-slate-900 text-white"><Bot className="h-6 w-6" /></div>
              <h2 className="mt-5 font-display text-xl font-semibold">What would you like to know about your cloud?</h2>
              <p className="mt-2 text-sm text-slate-500">Read-only mode: anything that would create, modify or delete a resource is shown as a suggested command, never executed.</p>
              <div className="mt-8 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} type="button" onClick={() => ask(s)} data-testid="patrice-suggestion" className="border border-slate-200 bg-white px-4 py-3 text-left text-sm text-slate-700 transition-colors hover:border-slate-900 hover:bg-slate-50">{s}</button>
                ))}
              </div>
            </div>
          ) : (
            <div className="mx-auto flex max-w-4xl flex-col gap-5">
              {messages.map((m) => <Message key={m.id} m={m} />)}
              {busy && (
                <div className="flex gap-3" data-testid="patrice-thinking">
                  <div className="grid h-8 w-8 shrink-0 place-items-center bg-slate-900 text-white"><Bot className="h-4 w-4" /></div>
                  <div className="inline-flex items-center gap-2 border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Patrice is planning the query and running it…</div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 bg-white px-10 py-4">
          <div className="mx-auto flex max-w-4xl items-end gap-3">
            <Textarea value={question} onChange={(e) => setQuestion(e.target.value)} onKeyDown={onKey} rows={2} placeholder="Ask Patrice… e.g. “Which VMs are stopped in westeurope?”" className="min-h-[56px] flex-1 resize-none border-slate-200 text-sm" data-testid="patrice-input" disabled={busy} />
            <Button onClick={() => ask()} disabled={busy || !question.trim()} className="h-[56px] bg-slate-900 px-5 text-white hover:bg-slate-800" data-testid="patrice-send">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizontal className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
